import { useEffect, useState, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import { DIVISIONS } from "../../../constants/divisions";
import { RECRUITMENT_STAGES } from "../../../constants/recruitmentStages";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import { SelectPopover } from "../../../components/SelectPopover";
import "./ResolveSubmissionModal.css";

export interface ResolveSubmissionPayload {
  recruitmentStage: string;
  projectedDivisions: string[];
  notes?: string;
}

interface ResolveSubmissionModalProps {
  isOpen: boolean;
  playerName: string;
  saving?: boolean;
  onSubmit: (payload: ResolveSubmissionPayload) => Promise<void> | void;
  onClose: () => void;
}

export const ResolveSubmissionModal = ({
  isOpen,
  playerName,
  saving,
  onSubmit,
  onClose,
}: ResolveSubmissionModalProps) => {
  const [recruitmentStage, setRecruitmentStage] = useState("");
  const [projectedDivisions, setProjectedDivisions] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setRecruitmentStage("");
      setProjectedDivisions([]);
      setNotes("");
      setError("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !saving) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose, saving]);

  const dialogRef = useRef<HTMLFormElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  const canSubmit =
    !saving && recruitmentStage.length > 0 && projectedDivisions.length > 0;

  const toggleDivision = (d: string) => {
    setProjectedDivisions((prev) => {
      if (prev.includes(d)) return prev.filter((x) => x !== d);
      if (prev.length >= 3) return prev;
      return [...prev, d];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    try {
      await onSubmit({
        recruitmentStage,
        projectedDivisions,
        notes: notes.trim() || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create player.");
    }
  };

  const handleOverlay = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !saving) onClose();
  };

  return (
    <div className="resolve-submission-modal-overlay" onClick={handleOverlay}>
      <form ref={dialogRef} tabIndex={-1}
        className="resolve-submission-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Create player from submission"
        onSubmit={handleSubmit}
      >
        <div className="resolve-submission-modal__header">
          <h2 className="resolve-submission-modal__title">
            Create player: {playerName}
          </h2>
          {!saving && <ModalCloseButton onClick={onClose} />}
        </div>

        <div className="resolve-submission-modal__body">
          <p className="resolve-submission-modal__help">
            Set recruitment stage and projected division(s) before adding this
            player to the CRM.
          </p>

          <div className="resolve-submission-modal__field">
            <label htmlFor="resolve-submission-modal-recruitment-stage-span-classname-r-0" className="resolve-submission-modal__label">
              Recruitment Stage <span className="resolve-submission-modal__required">*</span>
            </label>
            <SelectPopover
              id="resolve-submission-modal-recruitment-stage-span-classname-r-0"
              options={RECRUITMENT_STAGES}
              value={recruitmentStage}
              onChange={setRecruitmentStage}
              placeholder="Select stage..."
              disabled={saving}
              fullWidth
            />
          </div>

          <div className="resolve-submission-modal__field">
            <span className="resolve-submission-modal__label" id="resolve-submission-modal-projected-divisions-up-to-3-0">
              Projected Divisions (up to 3) <span className="resolve-submission-modal__required">*</span>
            </span>
            <div className="resolve-submission-modal__chips" role="group" aria-labelledby="resolve-submission-modal-projected-divisions-up-to-3-0">
              {DIVISIONS.map((d) => {
                const selected = projectedDivisions.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    className={`resolve-submission-modal__chip${
                      selected ? " resolve-submission-modal__chip--selected" : ""
                    }`}
                    disabled={saving || (!selected && projectedDivisions.length >= 3)}
                    onClick={() => toggleDivision(d)}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="resolve-submission-modal__field">
            <label htmlFor="resolve-submission-modal-notes-span-classname-resolve-submi-1" className="resolve-submission-modal__label">
              Notes <span className="resolve-submission-modal__optional">(optional)</span>
            </label>
            <textarea id="resolve-submission-modal-notes-span-classname-resolve-submi-1"
              className="resolve-submission-modal__textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
            />
          </div>

          {error && <p className="resolve-submission-modal__error">{error}</p>}
        </div>

        <div className="resolve-submission-modal__footer">
          <button
            type="button"
            className="resolve-submission-modal__btn resolve-submission-modal__btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="resolve-submission-modal__btn resolve-submission-modal__btn--primary"
            disabled={!canSubmit}
          >
            {saving ? "Creating..." : "Create Player"}
          </button>
        </div>
      </form>
    </div>
  );
};
